// The metricsIQ screens, as the wire delivers them.
//
// Beside data/reads.ts and following its rules exactly — types are the
// BACKEND's field names, snake_case and all; quantities are STRINGS and are
// never parsed to be displayed; moments are ISO-8601 with an offset and are
// turned into words on this side; and an absence is `null`, never `0` and
// never `""`. See that module's header for why each of those is the way round
// it is; the reasons do not change because the reader is a colleague rather
// than a customer. If anything they get stronger: this is the surface somebody
// reconciles a customer's usage against when the customer says a figure is
// wrong.
//
// SEPARATE from reads.ts rather than added to it, because every read here
// crosses customer boundaries and every read there does not. Each of these
// three writes rows into the see-all audit trail before it answers — that is
// what `across_all_customers()` does on the other side — and one HTTP request
// per screen is the audit design rather than a preference for chunky
// endpoints: one person opened one screen for one stated reason is the
// artefact the trail has always held, and four fetches per screen would turn
// it into four reasons. See backend operations/portal_reads.py.
//
// **A 404 from any of these three is a refusal, not a missing page, and it
// must never be read as "the token needs renewing".** The staff endpoints
// answer 404 to everything but a live staff token — no token, a customer's
// token, a withdrawn grant alike — because a resource outside your scope does
// not exist for you (P-9 404-never-403), and answering 403 would tell whoever
// walked the address space that there is an operations surface here. Nothing
// in this app renews on a 404: `authorizedFetch` retries on 401 and on 401
// only, so a 404 arrives at the screen as `PortalRequestFailed(404)` and is
// shown as the refusal it is. And for a customer's session the question never
// arises, because the nav is gated on the identity's shape and the routes
// redirect — the request is not issued at all.

import {
  API_BASE,
  PortalRequestFailed,
  jsonOrRefusal,
  type AuthorizedFetch,
} from '../auth/api';
import { formerQuery, type Retired } from './reads';

/**
 * The customer that replaced this one, where one did.
 *
 * Carried as a row rather than as a bare identifier because it is what the
 * screen has to draw. A superseded customer is kept — it is the record of how a
 * tenant arrived — hidden with the other former customers by default and, when
 * shown, marked in the Status column so it does not read as a live customer
 * beside the one that replaced it. Two lookalikes with no way to tell them
 * apart is what sent the last person to the wrong one.
 */
export interface SupersededBy {
  public_id: string;
  display_name: string;
}

/** One customer's line on the all-customers screen, with their totals. */
export interface CustomerSummary {
  public_id: string;
  display_name: string;
  /**
   * Every sales channel this customer holds a subscription on, sorted, or an
   * empty array where they hold none.
   *
   * All of them rather than one, because a customer may hold subscriptions on
   * more than one channel and naming whichever sorted first would be a claim
   * about where their money comes from. Nothing on `internal` or `seeded` can
   * bill — no adapter is registered for either — so on a list that also
   * carries paying customers this is the difference between a tenant we are
   * hosting and revenue.
   */
  channels: string[];
  superseded_by: SupersededBy | null;
  /**
   * Set where this customer has been retired — marked, not deleted, by
   * `retire_customer`. Hidden from the list by default; see `Retired`.
   */
  retired: Retired | null;
  subscription_count: number;
  /** Running instances by default; every instance under "Show former customers". */
  instance_count: number;
  /**
   * Everything this customer has ever metered, as an exact decimal string, or
   * `null` where they have metered nothing. Not `"0"`: a customer who has
   * reported nothing and a customer whose reports totalled zero are different
   * facts, and only the second is a figure.
   */
  usage_total: string | null;
  latest_usage_hour: string | null;
}

/** A metering silence alert nobody has resolved yet, on any customer. */
export interface OpenSilenceAlert {
  public_id: string;
  /** The instance's name. A name, not a row — the screen shows no more. */
  instance: string;
  usage_hour: string | null;
  detected_at: string | null;
}

export interface AllCustomersScreen {
  /** When this answer was assembled. The screen is a read, not a live view. */
  as_of: string;
  /** How many former customers — retired or superseded — the default left out of `customers`. */
  former_hidden: number;
  customers: CustomerSummary[];
  open_alerts: OpenSilenceAlert[];
}

/** Who the drill-in is about. */
export interface CustomerIdentification {
  public_id: string;
  display_name: string;
  created_at: string | null;
  /** The drill-in always answers, retired or not; this says which. */
  retired: Retired | null;
}

/** One agreement this customer holds. */
export interface CustomerSubscription {
  public_id: string;
  channel: string;
  /**
   * The channel's own reference for the agreement. Empty where the channel
   * gave none — an empty string rather than `null`, because the column is a
   * blank-able text field on the other side and this type says what arrives
   * rather than what would have been tidier.
   */
  agreement_ref: string;
  /** The core's own word for the state. Translating it is the screen's job. */
  state: string;
  started_at: string | null;
  billing_mode: BillingModeRecord;
  agreement_kind: AgreementKindRecord;
}

/**
 * Whose money an agreement moves, and who last said so.
 *
 * `customer` is every agreement's default. `internal` is an agreement metricsIQ
 * holds with itself: its metering faults still alarm, on the internal topic
 * rather than the operator's. An agreement on the `internal` channel reads
 * `internal` by definition, with `changed_at` null, and has no kind to set.
 */
export interface AgreementKindRecord {
  kind: 'customer' | 'internal';
  changed_at: string | null;
  changed_by: string;
  reason: string;
}

/**
 * Whether an agreement's usage reaches its channel, and who last said so.
 *
 * `live` is every agreement's default. `withheld` means everything up to the
 * channel call runs and the call does not: the figures are ledgered and shown,
 * marked withheld, and never billed later. `changed_at`, `changed_by` and
 * `reason` are null and empty for an agreement that has always been live.
 */
export interface BillingModeRecord {
  mode: 'live' | 'withheld';
  changed_at: string | null;
  changed_by: string;
  reason: string;
}

/** One instance running for this customer. */
export interface CustomerInstance {
  public_id: string;
  name: string;
  client_id: string;
  state: string;
  /**
   * The agreement this instance runs under, by identifier.
   *
   * **Deliberately not the same shape as `FleetInstance.subscription` in
   * reads.ts, and the two must not be unified.** On the customer's own fleet
   * screen a subscription is a whole nested row, because that screen shows the
   * agreement's channel and state beside each instance. Here it is the
   * identifier alone, because this screen already lists the subscriptions in
   * full in their own table above and repeating them per instance would be two
   * sources for one fact. Same word, two different amounts of data, on purpose.
   */
  subscription_public_id: string;
  retired: Retired | null;
}

/** What one dimension has recorded, over this customer's whole history. */
export interface CustomerUsageDimension {
  dimension: string;
  total: string | null;
  /** How many hourly rows make up that total. A count, so a number. */
  hours: number;
  latest_hour: string | null;
}

/** One recent hour of usage, as it was reported. */
export interface CustomerUsageHour {
  usage_hour: string | null;
  instance: string;
  dimension: string;
  quantity: string | null;
  received_at: string | null;
}

/** One recent handing-over of usage to a sales channel, and what came back. */
export interface CustomerSubmission {
  public_id: string;
  channel: string;
  quantity_submitted: string | null;
  /** The core's own word. Empty means the channel has not answered yet. */
  outcome: string;
  submitted_at: string | null;
}

export interface CustomerHoldingsScreen {
  /** Whether this reader holds the act-for-customers role, and so may change a billing mode here. */
  may_act: boolean;
  customer: CustomerIdentification;
  subscriptions: CustomerSubscription[];
  instances: CustomerInstance[];
  usage_by_dimension: CustomerUsageDimension[];
  recent_usage: CustomerUsageHour[];
  recent_submissions: CustomerSubmission[];
}

/** One read that crossed customer boundaries, as the trail recorded it. */
export interface SeeAllAccess {
  occurred_at: string | null;
  /** Who read. The username as the trail holds it, not a display name. */
  actor: string;
  /** Which table, as `app_label.ModelName`. */
  model_label: string;
  /** Why, in the reader's own words — or the screen's, where a screen read. */
  reason: string;
}

export interface SeeAllAuditScreen {
  accesses: SeeAllAccess[];
}

/**
 * One deployment somebody asked us to stand up.
 *
 * `settled_at` is null while it is still waiting, which is the field the
 * screen sorts a queue out of a record by. `requested_by` is the address to
 * reply to, resolved on the backend: the request itself records only which
 * user asked.
 */
export interface DeploymentRequest {
  public_id: string;
  state: string;
  instance_name: string;
  /**
   * The address the customer named for the deployment's FIRST LOGIN — the one
   * field on this row that leaves the screen by hand, because the platform
   * deploy takes it as an input and creates that account.
   *
   * Not the same fact as `requested_by`, and the difference is the whole
   * reason it is asked: a buyer may be provisioning for a colleague. Where
   * they match, they match because the person said so.
   *
   * Empty on a request filed before the form began asking — never `null`, and
   * empty means NOBODY WAS ASKED rather than somebody declining to answer, so
   * a screen must not quietly draw `requested_by` in its place.
   */
  initial_user_email: string;
  /**
   * THE NAME THE DEPLOYMENT WILL ANSWER TO, which is not `instance_name`
   * above.
   *
   * `instance_name` is free text the customer typed. This is what it reduced
   * to, and it is the identifier both platform workflows key on — the
   * namespace an operator must vend and deploy under for this request ever to
   * be answered. "Marketplace Test Two" reduces to `marketplace-test-two`; a
   * namespace of `mktest2-dev` would never be heard from, and the request
   * would sit at Requested forever with nothing anywhere saying why.
   *
   * Empty on a request recorded before the link existed. Those have no
   * deployment row at all, which is a different fact from a deployment that
   * has not reported.
   */
  deployment_name: string;
  deployment_state: string;
  /** Whether that deployment has ever presented its credential to us. */
  reported: boolean;
  /**
   * Whether the silence has gone on longer than anybody should wait quietly.
   *
   * NOT a verdict that the deploy failed — nothing knows that, and a request
   * has no path to Declined because this service has no staff write surface
   * to record one. It says the wait is worth looking at.
   */
  overdue: boolean;
  /**
   * Live deployments under the same subscription that no request created.
   *
   * One of these beside a request that is still waiting is almost always one
   * deployment and one request that failed to find each other — a vend under
   * a namespace that does not match what the customer typed. Deliberately not
   * matched up: guessing would settle a request on a resemblance of spelling,
   * which is the bug.
   */
  deployments_no_request_asked_for: string[];
  decision_pack: string;
  region: string;
  note: string;
  requested_at: string;
  settled_at: string | null;
  customer: string;
  customer_public_id: string;
  /**
   * The CUSTOMER's retirement, where there is one. A request is not a thing
   * that retires; its customer is, and a retired customer's requests leave the
   * list with it by default.
   */
  retired: Retired | null;
  subscription: string;
  channel: string;
  requested_by: string;
}

export interface DeploymentRequestsScreen {
  /**
   * Whether THIS reader may act on what they are reading.
   *
   * Reading the fleet and changing it are separate roles, so a see-all reader
   * opens this screen and may change nothing. The screen draws its acting
   * affordances only when this is true — offering a button that answers 404
   * is worse than offering none.
   */
  may_act: boolean;
  /** How many requests of former customers — retired or superseded — the default left out. */
  former_hidden: number;
  requests: DeploymentRequest[];
}

/**
 * The run of calendar days a cost read is asked for: `from` and `to`, both
 * `YYYY-MM-DD`, both inclusive, UTC.
 *
 * The query string's own two parameters, carried as the screen holds them. A
 * read takes this or `null`, and `null` is "did not ask" — the service answers
 * the month to date, so a session left open across midnight on the 1st follows
 * the service rather than pinning the month it was opened in.
 */
export interface DayRangeQuery {
  from: string;
  to: string;
}

/**
 * The range the service actually cut the figures on, echoed back.
 *
 * Carried on the wire rather than recomputed here so the caption under the
 * control cannot drift from the arithmetic it describes: when nothing was
 * asked, this is where the screen learns which days "the month to date" meant.
 */
export interface DayRange {
  first: string;
  last: string;
  /** How many calendar days, `first` and `last` included. */
  days: number;
}

/**
 * What one instance cost metricsIQ over the range.
 *
 * `usd` is `null` when `days_posted` is zero — a range in which the instance
 * posted no day has NO figure, and the service says so rather than sending
 * `"0"`. A posted day with no cost in it counts as posted and contributes
 * zero; a day nobody posted contributes nothing and is not counted. The
 * difference is the whole of what this screen exists to keep visible, so
 * `days_posted` of `days_in_window` is carried beside every figure.
 */
export interface FleetCostWindow {
  usd: string | null;
  days_posted: number;
  days_in_window: number;
}

/**
 * One `(model_id, workload)` of a day's cost, as the instance posted it.
 *
 * `model_id` is `""` where the platform's ledger could not name the model —
 * and for every `runtime-execution` line, since compute has no model. The
 * screen reads the empty string as "unresolved model"; the wire passes through
 * what was stored. Tokens and counts are numbers; `usd` is the eight-place
 * decimal string the store holds and is never parsed to be shown.
 */
export interface FleetCostLine {
  model_id: string;
  workload: string;
  uncached_input_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  usd: string;
  message_count: number;
  unpriced_message_count: number;
}

/**
 * One calendar day of the range, posted or not.
 *
 * A sum on `posted`, and the unposted arm carries nothing but the day: there
 * is no figure for a day nobody posted, and a type that offered `usd` on it
 * would be a type a screen could read a zero out of. A posted day carries its
 * lines — an empty list for a day the ledger found no cost in, which is a
 * statement, not an absence.
 */
export type FleetCostDay =
  | { day: string; posted: false }
  | {
      day: string;
      posted: true;
      posted_at: string | null;
      usd: string;
      lines: FleetCostLine[];
    };

/** One instance on the Fleet Costs screen. */
export interface FleetCostInstance {
  instance: {
    public_id: string;
    name: string;
    /** `null` for an instance that has not yet declared its billing identity. */
    client_id: string | null;
    customer: string;
  };
  retired: Retired | null;
  /** The one figure for the range: the sum of the days posted inside it. */
  usd: FleetCostWindow;
  /**
   * The latest reconciliation ratio per month, keyed `YYYY-MM`, with one key
   * for every month the range touches. `null` where no day of the month inside
   * the range was posted, or where the newest posted day carried no
   * reconciliation — the ledger had no run for the month at the time.
   */
  reconciliation: Record<string, { ratio: string; day: string } | null>;
  /** Every calendar day of the range, oldest first. */
  days: FleetCostDay[];
}

export interface FleetCostsScreen {
  range: DayRange;
  /** How many retired instances the default left out of `instances` — an instance's only way of being former. */
  former_hidden: number;
  instances: FleetCostInstance[];
}

/**
 * The query string for a screen's parameters, or `''` where none is set.
 *
 * Built once so every read spells `show_former` the same way and no read
 * writes `?` in front of nothing.
 */
function query(...parts: string[]): string {
  const set = parts.filter((part) => part !== '');
  return set.length === 0 ? '' : `?${set.join('&')}`;
}

/**
 * Every customer, with what they hold and what they have used.
 *
 * Module-level, like every read in reads.ts, because `usePortalRead` holds it
 * in an effect's dependency array — an inline arrow would re-run the request
 * on every render and never settle. Opening this screen writes five rows into
 * the see-all trail, one per table read, under one shared reason.
 *
 * `showFormer` brings back the former customers — retired or superseded — the
 * default hides. A page offering the toggle memoises the call per setting, and
 * flipping it is a second screen-open in the trail, which is what it was.
 */
export function readAllCustomers(
  authorizedFetch: AuthorizedFetch,
  showFormer = false,
): Promise<AllCustomersScreen> {
  return read<AllCustomersScreen>(
    authorizedFetch,
    `metricsiq/customers/${query(formerQuery(showFormer))}`,
  );
}

/**
 * One customer in full, seen from outside.
 *
 * Takes the identifier, so a page memoises the call per customer rather than
 * this module holding one function per customer. Writes exactly one row into
 * the trail — the read that resolves the customer — and every read after it is
 * scoped to that customer, so it is as narrow as one of their own members'.
 *
 * An identifier nobody holds answers 404, the same as a token that may not
 * look: the two are not distinguished on the wire and are not distinguished
 * here either.
 */
export function readCustomerHoldings(
  authorizedFetch: AuthorizedFetch,
  publicId: string,
): Promise<CustomerHoldingsScreen> {
  return read<CustomerHoldingsScreen>(
    authorizedFetch,
    `metricsiq/customers/${encodeURIComponent(publicId)}/`,
  );
}

/**
 * Every deployment a customer has asked for, newest first.
 *
 * The screen that did not exist. A customer could file a deployment request
 * and nobody at metricsIQ could see it: this surface read customers,
 * subscriptions, instances, usage, submissions and alerts, and never
 * provisioning requests — so the only way to reach one was a management
 * command that could not list them.
 *
 * Writes exactly one audit row, like every other cross-customer read here.
 */
export function readDeploymentRequests(
  authorizedFetch: AuthorizedFetch,
  showFormer = false,
): Promise<DeploymentRequestsScreen> {
  return read<DeploymentRequestsScreen>(
    authorizedFetch,
    `metricsiq/deployments/${query(formerQuery(showFormer))}`,
  );
}

/**
 * The see-all trail: who read across customers, and why.
 *
 * The one screen of the three that writes NO audit row, and that is the
 * ported behaviour rather than an oversight — the trail belongs to no
 * customer, so reading it crosses no boundary, and a row written every time
 * somebody opened the log would fill the log with lookings rather than
 * readings.
 */
export function readSeeAllAudit(
  authorizedFetch: AuthorizedFetch,
): Promise<SeeAllAuditScreen> {
  return read<SeeAllAuditScreen>(authorizedFetch, 'metricsiq/audit/');
}

/**
 * What each instance cost metricsIQ, day by day, over one range of days.
 *
 * metricsIQ's own book, never a customer's bill: the figure a customer's Fleet
 * page does not carry and must not. Writes three audit rows — instances, days,
 * lines — under one reason.
 *
 * `range` is `null` for "did not ask", which the service answers with the
 * month to date; the range it cut on comes back under `range` either way. The
 * page wraps this in `useCallback` on the selection, so `usePortalRead` sees a
 * new read exactly when the selection changes — and when `showFormer` does,
 * which brings back the retired instances the default leaves out.
 */
export function readFleetCosts(
  authorizedFetch: AuthorizedFetch,
  range: DayRangeQuery | null,
  showFormer = false,
): Promise<FleetCostsScreen> {
  return read<FleetCostsScreen>(
    authorizedFetch,
    `metricsiq/fleet-costs/${dayRangeQuery(range, showFormer)}`,
  );
}

/**
 * Fetch the range's CSV export with the bearer and hand it to the browser.
 *
 * A download rather than a link, because the credential is a header the
 * browser never attaches on its own: an `<a href>` to the export would arrive
 * with no token and be answered 404, the same answer the address gives
 * everybody it does not serve. So the bytes are fetched as the screen's other
 * reads are, wrapped in a Blob, and saved under the name the service chose.
 * The object URL is revoked once the click has been dispatched — the Blob is
 * the browser's by then.
 *
 * `range` is the same selection the screen was read with, `null` included, so
 * the file holds exactly the days on the screen; `showFormer` likewise, so it
 * holds exactly the instances.
 *
 * @throws PortalRequestFailed with the service's wording, as every read here.
 */
export async function downloadFleetCostsExport(
  authorizedFetch: AuthorizedFetch,
  range: DayRangeQuery | null,
  showFormer = false,
): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE}/metricsiq/fleet-costs/export/${dayRangeQuery(range, showFormer)}`,
    { method: 'GET' },
  );
  await saveDownload(response, 'fleet-costs.csv');
}

/**
 * What one customer is billed over the range, as the pricing module totals it.
 *
 * `amount` is `null` where no line could be priced, where the lines are in two
 * currencies (`mixed_currencies`), or where the customer is not billable at
 * all — and never `"0"` for any of those: "we cannot say" and "nothing was
 * owed" are different answers. `estimate` is always true and `basis` says how
 * the figure was arrived at; the screen passes the mark through rather than
 * deciding where to show it.
 */
export interface FleetRevenueAmount {
  amount: string | null;
  currency: string | null;
  mixed_currencies: boolean;
  estimate: boolean;
  basis: string;
  /** Lines the catalogue could not price, and so left out of the total. */
  unpriced_lines: number;
}

/**
 * The hours a channel has not yet answered for, priced at the catalogue rate.
 *
 * A weaker estimate than the amount — it rests on the exact ledger quantity
 * rather than on what the channel took — so it is carried apart from the
 * amount and never folded into it. `amount` is `null` where no record awaits,
 * where a dimension is unpriced, or for a customer nothing here can bill.
 */
export interface FleetRevenuePending {
  records: number;
  amount: string | null;
  currency: string | null;
  estimate: boolean;
  basis: string;
}

/**
 * How one of the customer's channels stands over the range.
 *
 * `not_billable`: no adapter is configured for the channel. `pending`: a record
 * awaits an answer, which wins over the others while any does. `withheld`: a
 * record in the range was packaged and deliberately not sent, because the
 * agreement is withheld. `carried_forward`: the range's recorded quantity
 * exceeds what the channel settled, so a remainder is riding forward.
 * `settled`: nothing outstanding.
 */
export interface FleetRevenueSettlement {
  channel: string;
  state: 'settled' | 'pending' | 'withheld' | 'carried_forward' | 'not_billable';
}

/** What was packaged and not sent over the range, per dimension: never inside the amount. */
export interface FleetRevenueWithheld {
  dimension: string;
  quantity: string;
}

/** Recorded minus settled over the range, per dimension — the Metering page's "Carried forward". */
export interface FleetRevenueCarry {
  dimension: string;
  quantity: string;
}

/**
 * What the customer's instances cost metricsIQ over the range, summed.
 *
 * The same figure Fleet Costs shows beside each instance, from the same
 * function, added up. `usd` is `null` where no instance posted a day in the
 * range. `days_posted` counts instance-days, read against `days_in_window`
 * times `instances`.
 */
export interface FleetRevenueCost {
  usd: string | null;
  days_posted: number;
  days_in_window: number;
  instances: number;
}

/** One priced line — a period, an agreement and a dimension — as the Billing page has it. */
export interface FleetRevenueLine {
  /** The month, `YYYY-MM`. */
  period: string;
  agreement_ref: string;
  channel: string;
  pack_name: string | null;
  dimension: string;
  unit_short: string | null;
  quantity: string;
  hours: number;
  priced: boolean;
  rate: string | null;
  amount: string | null;
  currency: string | null;
  estimate: boolean;
  basis: string;
}

/** One customer on the Fleet Revenue screen. */
export interface FleetRevenueCustomer {
  customer: {
    public_id: string;
    display_name: string;
  };
  retired: Retired | null;
  /** The customer that replaced this one, where one did — the same row All Customers carries. */
  superseded_by: SupersededBy | null;
  /** Whether any of the customer's agreements is on a channel with an adapter. */
  billable: boolean;
  /** Settled submissions in the range. */
  usage_events: number;
  amount: FleetRevenueAmount;
  pending: FleetRevenuePending;
  settlement: FleetRevenueSettlement[];
  withheld: FleetRevenueWithheld[];
  carry: FleetRevenueCarry[];
  cost: FleetRevenueCost;
  /** Amount minus cost, as a decimal string, only when both exist in the cost's currency. */
  margin: string | null;
  lines: FleetRevenueLine[];
}

/** The footer: the figures that exist, summed through the same functions. */
export interface FleetRevenueTotal {
  amount: FleetRevenueAmount;
  pending: FleetRevenuePending;
  cost: FleetRevenueCost;
  margin: string | null;
  customers_with_figure: number;
  customers_not_billable: number;
}

export interface FleetRevenueScreen {
  range: DayRange;
  /** How many former customers — retired or superseded — the default left out of `customers`. */
  former_hidden: number;
  customers: FleetRevenueCustomer[];
  total: FleetRevenueTotal;
}

/**
 * What each customer is billed over one range of days, beside what they cost.
 *
 * The per-customer Billing and Metering figures read across every customer,
 * which no customer's own address can do. Writes seven audit rows — customers,
 * subscriptions, submissions, usage, instances, days, lines — under one reason.
 * The same range and toggle parameters as Fleet Costs, built by the same
 * function, so the two screens cannot be cut on different days.
 */
export function readFleetRevenue(
  authorizedFetch: AuthorizedFetch,
  range: DayRangeQuery | null,
  showFormer = false,
): Promise<FleetRevenueScreen> {
  return read<FleetRevenueScreen>(
    authorizedFetch,
    `metricsiq/fleet-revenue/${dayRangeQuery(range, showFormer)}`,
  );
}

/**
 * Fetch the range's revenue CSV with the bearer and hand it to the browser.
 *
 * The same selection the screen was read with, `null` and the toggle included,
 * so the file holds exactly the customers and lines on the screen.
 *
 * @throws PortalRequestFailed with the service's wording, as every read here.
 */
export async function downloadFleetRevenueExport(
  authorizedFetch: AuthorizedFetch,
  range: DayRangeQuery | null,
  showFormer = false,
): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE}/metricsiq/fleet-revenue/export/${dayRangeQuery(range, showFormer)}`,
    { method: 'GET' },
  );
  await saveDownload(response, 'fleet-revenue.csv');
}

/**
 * Save a CSV response under the name the service chose.
 *
 * A download rather than a link, because the credential is a header the
 * browser never attaches on its own: an `<a href>` to the export would arrive
 * with no token and be answered 404, the same answer the address gives
 * everybody it does not serve. So the bytes are fetched as the screen's other
 * reads are, wrapped in a Blob, and saved under the name the service chose.
 * The object URL is revoked once the click has been dispatched — the Blob is
 * the browser's by then.
 */
async function saveDownload(response: Response, fallbackName: string): Promise<void> {
  const bytes = await bytesOrRefusal(response);
  const name = filenameFrom(response.headers.get('Content-Disposition'), fallbackName);

  const url = URL.createObjectURL(bytes);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * The range parameters and the former toggle as one query string, or nothing
 * when nothing was asked. One place for every day-ranged read — Fleet Costs,
 * Fleet Revenue and their exports — so a screen and its export, or two screens
 * side by side, cannot be cut on two different ranges or two different sets of
 * rows.
 */
function dayRangeQuery(range: DayRangeQuery | null, showFormer: boolean): string {
  return query(
    range === null
      ? ''
      : `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    formerQuery(showFormer),
  );
}

/**
 * The filename the service named in `Content-Disposition`, or the plain one.
 *
 * The service names the range in the file
 * (`fleet-costs-2026-10-01-to-2026-10-07.csv`), and a download that lost it
 * would be a file nobody could tell from last week's. Read off the header
 * rather than reassembled here so the two cannot drift.
 */
function filenameFrom(disposition: string | null, fallbackName: string): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match ? match[1] : fallbackName;
}

/**
 * The body of a non-JSON portal response as a Blob, or the refusal as an error.
 *
 * `jsonOrRefusal` is for the screens; the export is bytes. The refusal half is
 * the same: a non-2xx carries `{"error": …}` or `{"detail": …}` in the
 * service's own words, and those are what the screen shows.
 */
async function bytesOrRefusal(response: Response): Promise<Blob> {
  if (!response.ok) {
    let wording = `The service answered ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string; detail?: string };
      const said = body.error ?? body.detail;
      if (said) {
        wording = said.charAt(0).toUpperCase() + said.slice(1);
      }
    } catch {
      // No JSON body: a proxy or gateway answered. The status is all there is.
    }
    throw new PortalRequestFailed(response.status, wording);
  }
  return response.blob();
}

async function read<T>(
  authorizedFetch: AuthorizedFetch,
  screen: string,
): Promise<T> {
  const response = await authorizedFetch(`${API_BASE}/${screen}`, {
    method: 'GET',
  });
  // `jsonOrRefusal` turns any non-2xx into `PortalRequestFailed` carrying the
  // status — including the 404 these three answer a caller who may not look.
  // No renewal, no retry: see this module's header.
  return jsonOrRefusal<T>(response);
}

/**
 * Answer a deployment request that is not going to be built.
 *
 * The path to Declined the fleet never had: a deploy that fails never boots,
 * so it never authenticates, so nothing settles the request and it sits at
 * Requested looking exactly like one still running.
 *
 * DECLINED ONLY, and never Completed — a request a person marked completed
 * would be indistinguishable from one a deployment settled by reporting for
 * itself, which is the failure this whole surface exists to end.
 *
 * `reason` is required and goes into the record verbatim. It is not a
 * formality: these acts exist because something happened the service could
 * not observe for itself, and this sentence is the only account of what.
 */
export function declineDeploymentRequest(
  authorizedFetch: AuthorizedFetch,
  request: string,
  reason: string,
): Promise<DeploymentActRecorded> {
  return act<DeploymentActRecorded>(
    authorizedFetch,
    'metricsiq/deployments/decline/',
    { request, reason },
  );
}

/**
 * Say which namespace this request's deployment will actually answer to.
 *
 * The correlation between a request and its platform namespace was a
 * coincidence of spelling: the deployment is named for the slug the
 * CUSTOMER's typed name reduces to, and the namespace is chosen by whoever
 * vends. "Marketplace Test Two" reduces to `marketplace-test-two`; the
 * namespace was `mktest2-dev`, and a deployment under it would never have
 * been heard from.
 *
 * This is where the operator who vended says so, making the correlation a
 * recorded decision. It renames the row the request created rather than
 * pointing the request at another one — the row carries the first-login
 * address the customer named, and any credential minted for it is keyed to
 * it.
 */
export function bindDeploymentRequest(
  authorizedFetch: AuthorizedFetch,
  request: string,
  deploymentName: string,
  reason: string,
): Promise<DeploymentActRecorded> {
  return act<DeploymentActRecorded>(
    authorizedFetch,
    'metricsiq/deployments/bind/',
    { request, deployment_name: deploymentName, reason },
  );
}

/**
 * Set whether one agreement's usage is handed to its channel.
 *
 * `withheld` runs everything up to the channel call and sends nothing; `live`
 * bills from the next hour on, and the hours withheld before stay withheld.
 * The reason is required and recorded verbatim against the signed-in name.
 * Withholding the records a channel already holds unconfirmed is NOT done
 * here: that is the `set_billing_mode --convert-pending` step of the runbook.
 */
export function setSubscriptionBillingMode(
  authorizedFetch: AuthorizedFetch,
  subscription: string,
  mode: BillingModeRecord['mode'],
  reason: string,
): Promise<BillingModeRecorded> {
  return act<BillingModeRecorded>(
    authorizedFetch,
    'metricsiq/subscriptions/billing-mode/',
    { subscription, mode, reason },
  );
}

/**
 * Set whether one agreement is a customer's or our own.
 *
 * `internal` moves its metering alarms to the internal topic; `customer` moves
 * them back to the operator's. Nothing is suppressed either way. The reason is
 * required and recorded verbatim against the signed-in name.
 */
export function setSubscriptionAgreementKind(
  authorizedFetch: AuthorizedFetch,
  subscription: string,
  kind: AgreementKindRecord['kind'],
  reason: string,
): Promise<AgreementKindRecorded> {
  return act<AgreementKindRecorded>(
    authorizedFetch,
    'metricsiq/subscriptions/agreement-kind/',
    { subscription, kind, reason },
  );
}

/** What the service recorded for an agreement-kind change. */
export interface AgreementKindRecorded {
  subscription: string;
  agreement_kind: AgreementKindRecord['kind'];
  was: AgreementKindRecord['kind'];
  changed_at: string | null;
  changed_by: string;
  reason: string;
  recorded_as: string;
}

/** What the service recorded for a billing-mode change. */
export interface BillingModeRecorded {
  subscription: string;
  billing_mode: BillingModeRecord['mode'];
  was: BillingModeRecord['mode'];
  changed_at: string | null;
  changed_by: string;
  reason: string;
  recorded_as: string;
}

/** What the service says it recorded, echoed back so a screen can show it. */
export interface DeploymentActRecorded {
  request: string;
  recorded_as: string;
  state?: string;
  settled_at?: string | null;
  deployment_name?: string;
  was?: string;
}

/**
 * POST to one of the acting addresses.
 *
 * Separate from `read` above because these are POSTs and because their
 * refusals mean different things: a 409 is the service declining to do
 * something that no longer makes sense (a request already answered, a
 * deployment that has already reported in), which a screen shows to the
 * operator rather than treating as a fault. A 404 is the same answer the
 * reads give — and here it also covers a fleet READER, who may open every
 * screen and change nothing.
 */
async function act<T>(
  authorizedFetch: AuthorizedFetch,
  address: string,
  body: Record<string, string>,
): Promise<T> {
  const response = await authorizedFetch(`${API_BASE}/${address}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrRefusal<T>(response);
}
